import * as BookmarkTypes from 'common/types/reports/bookmark';

const DEFAULT_RETENTION_CLAUSE: Readonly<BookmarkTypes.BehaviorShowClause> = Object.freeze({
  type: BookmarkTypes.TopLevelMetricType.Metric,
  behavior: {
    type: BookmarkTypes.MetricType.Retention,
    name: null,
    retentionType: `birth`,
    retentionAlignmentType: BookmarkTypes.RetentionAlignmentType.Birth,
    retentionUnit: BookmarkTypes.BaseTimeUnit.Day,
    retentionUnboundedMode: `none`,
    retentionCustomBucketSizes: [],
    resourceType: BookmarkTypes.InsightsResourceType.Events,
    behaviors: [
      {
        type: BookmarkTypes.MetricType.Event,
        name: null,
        filters: [],
        filtersDeterminer: `all`,
      },
      {
        type: BookmarkTypes.MetricType.Event,
        name: null,
        filters: [],
        filtersDeterminer: `all`,
      },
    ],
  },
  measurement: {
    math: BookmarkTypes.RETENTION_MATH_TYPES.RETENTION_RATE,
    retentionBucketIndex: 0,
  },
} satisfies BookmarkTypes.BehaviorShowClause);

const DEFAULT_RETENTION_FREQUENCY: Readonly<BookmarkTypes.BehaviorShowClause> = Object.freeze({
  type: BookmarkTypes.TopLevelMetricType.Metric,
  behavior: {
    type: BookmarkTypes.MetricType.RetentionFrequency,
    name: null,
    retentionType: `addiction`,
    retentionAlignmentType: BookmarkTypes.RetentionAlignmentType.Birth,
    retentionUnit: BookmarkTypes.BaseTimeUnit.Day,
    retentionUnboundedMode: `none`,
    retentionCustomBucketSizes: [],
    resourceType: BookmarkTypes.InsightsResourceType.Events,
    behaviors: [
      {
        type: BookmarkTypes.MetricType.Event,
        name: null,
        filters: [],
      },
    ],
  },
  measurement: {
    math: BookmarkTypes.RETENTION_MATH_TYPES.RETENTION_RATE,
    retentionBucketIndex: 0,
  },
} satisfies BookmarkTypes.BehaviorShowClause);

const DEFAULT_FUNNEL_CLAUSE: Readonly<BookmarkTypes.BehaviorShowClause> = Object.freeze({
  type: BookmarkTypes.TopLevelMetricType.Metric,
  behavior: {
    type: BookmarkTypes.MetricType.Funnel,
    name: null,
    conversionWindowDuration: 7,
    conversionWindowUnit: BookmarkTypes.ConversionWindowUnit.Day,
    funnelOrder: BookmarkTypes.FunnelOrder.Loose,
    aggregateBy: [],
    resourceType: BookmarkTypes.InsightsResourceType.Events,
    behaviors: [
      {
        type: BookmarkTypes.MetricType.Event,
        name: null,
        filters: [],
      },
      {
        type: BookmarkTypes.MetricType.Event,
        name: null,
        filters: [],
      },
    ],
  },
  measurement: {
    math: BookmarkTypes.FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
    stepIndex: null,
  },
} satisfies BookmarkTypes.BehaviorShowClause);

const DEFAULT_SIMPLE_CLAUSE: Readonly<BookmarkTypes.BehaviorShowClause> = Object.freeze({
  type: BookmarkTypes.TopLevelMetricType.Metric,
  display: {},
  behavior: {
    type: BookmarkTypes.MetricType.Simple,
    name: null,
    resourceType: BookmarkTypes.InsightsResourceType.Events,
    behaviors: [
      {
        type: BookmarkTypes.MetricType.Event,
        name: null,
        filters: [],
      },
    ],
  },
  measurement: {
    math: BookmarkTypes.PRIMITIVE_MATH_TYPES.UNIQUE,
  },
} satisfies BookmarkTypes.BehaviorShowClause);

const DEFAULT_PROFILE_CLAUSE: Readonly<BookmarkTypes.BehaviorShowClause> = Object.freeze({
  type: BookmarkTypes.TopLevelMetricType.Metric,
  behavior: Object.freeze({
    type: BookmarkTypes.MetricType.People,
    name: `$all_people`,
    resourceType: BookmarkTypes.InsightsResourceType.People,
  }),
  measurement: Object.freeze({
    math: BookmarkTypes.PRIMITIVE_MATH_TYPES.TOTAL,
  }),
} satisfies BookmarkTypes.BehaviorShowClause);

const DEFAULT_COHORT_CLAUSE: Readonly<BookmarkTypes.BehaviorShowClause> = Object.freeze({
  type: BookmarkTypes.TopLevelMetricType.Metric,
  behavior: {
    type: BookmarkTypes.MetricType.Cohort,
    name: null,
    resourceType: BookmarkTypes.InsightsResourceType.Cohorts,
  },
  measurement: {
    math: BookmarkTypes.PRIMITIVE_MATH_TYPES.UNIQUE,
  },
} satisfies BookmarkTypes.BehaviorShowClause);

export const DEFAULT_CLAUSE_BY_TYPE: Readonly<
  Partial<Record<BookmarkTypes.MetricType, BookmarkTypes.BehaviorShowClause>>
> = {
  [BookmarkTypes.MetricType.Simple]: DEFAULT_SIMPLE_CLAUSE,
  [BookmarkTypes.MetricType.Funnel]: DEFAULT_FUNNEL_CLAUSE,
  [BookmarkTypes.MetricType.Retention]: DEFAULT_RETENTION_CLAUSE,
  [BookmarkTypes.MetricType.RetentionFrequency]: DEFAULT_RETENTION_FREQUENCY,
  [BookmarkTypes.MetricType.People]: DEFAULT_PROFILE_CLAUSE,
  [BookmarkTypes.MetricType.Cohort]: DEFAULT_COHORT_CLAUSE,
};

export const UI_STATE_METRIC_FIELDS: Array<
  keyof BookmarkTypes.BehaviorShowClause | keyof BookmarkTypes.FormulaShowClause
> = [`_idx`, `isExpanded`, `labelPrefix`, `formulaLabel`, `showClauseIndex`];

export const UI_STATE_BEHAVIOR_FIELDS: Array<keyof BookmarkTypes.Behavior> = [
  `unsavedId`,
  `search`,
  `dataset`,
  `profileType`,
  `projectId`,
];
